import { Events } from '../events/app-events';
import { requires } from '../mixins/dependency';
import { Defer, defer } from '../utils/async-store';
import { eventBus } from '../utils/event-bus';
import { BaseService } from './base-service';
import { ConfigService } from './config-service';

type Deps = {
  configService: ConfigService;
};

let globalDeferCount = 0;

export class ApiError<T = any> extends Error {
  response: Response;
  data: T;

  constructor(r: Response, data: T) {
    super('LHApiError');
    this.response = r;
    this.data = data;
  }
}

@requires(ConfigService.serviceName)
export class ApiService extends BaseService<Deps> {
  static serviceName = 'apiService';

  private endpoint: string;
  private lockCount = 0;
  private parallelRequestsCount = 0;
  private queue: Defer<any, any> = defer('api-service-initial-bootup');
  private static authToken: string = '';

  protected requestOpts = {
    credentials: <RequestCredentials>'include',
    method: 'GET',
    mode: <RequestMode>'cors',
    referrerPolicy: 'no-referrer',
    extraHeaders: <[string, string][]>[],
  };

  constructor(enpointPropertyName = 'lighthouseApiEndpoint', customPrefix: string = '') {
    super();
    const configService = this.deps.configService;
    this.endpoint = configService.get(enpointPropertyName) as string + '/' + (customPrefix || configService.get('apiPrefix'));
    this.queue.resolve(true);
  }

  public async waitForFlushComplete(): Promise<void> {
    if (this.parallelRequestsCount > 0) {
      return defer.recycle('api-service-parallel-requests');
    } else {
      return Promise.resolve();
    }
  }

  private setParallelRequestsCount(newValue: number) {
    this.parallelRequestsCount = Math.max(0, newValue);
    if (this.parallelRequestsCount === 0) {
      defer.recycle('api-service-parallel-requests').resolve(true);
    }
  }

  public getToken() {
    return ApiService.authToken;
  }

  public setToken(token: string) {
    ApiService.authToken = token;
  }

  public lock(loaderText = '') {
    this.lockCount++;
    document.body.setAttribute('is-busy', '');
    if (['Uploading image','Uploading image rotate text'].includes(loaderText)) document.body.setAttribute('is-busy-ocr', '');
    // we reset text display
    const resetLoaderText = ['Uploading image'];
    if (resetLoaderText.includes(loaderText)) loaderText = '';
    if (loaderText) document.querySelector('ahoy-loader')!.setAttribute('loader-text', loaderText);
    else document.querySelector('ahoy-loader')!.setAttribute('loader-text', '');
  }

  public unlock() {
    this.lockCount--;
    if (this.lockCount <= 0) {
      this.lockCount = 0;
      document.body.removeAttribute('is-busy');
      document.body.removeAttribute('is-busy-ocr');
    }
  }

  async upload(file: File, type: string) {
    const method = 'POST';
    const formData = new FormData();
    formData.append('file', file, file.name);
    // headers.append('content-type', 'Content-Type: multipart/form-data');
    const response = await fetch(`${this.endpoint}/files/${type}`, {
      credentials: 'include',
      method,
      mode: 'cors',
      referrerPolicy: 'no-referrer',
      body: formData,
    });
    return await response.json();
  }

  getAPIBaseUrl() {
    return this.endpoint;
  }

  async queueHttp<T = any>(path: string, data?: any) {
    await this.queue;
    const qName = `api-service-queue-${globalDeferCount++}`;
    this.queue = defer(qName);
    try {
      console.info(`Executing ${qName}`);
      return await this.http<T>(path, data);
    } finally {
      this.queue.resolve(true);
    }
  }

  /**
   * @throws {ApiError}
   * @throws {Error}
   */
  async http<T = any>(path: string, data?: any) {
    const headers = new Headers();
    this.requestOpts.extraHeaders.forEach(([h, c]) => headers.append(h, c));
  
    // Determine if the call should be POST or GET
    const isPostCall = data && !(data.isPostCall === false);
  
    // Check if isMobile needs to be appended to the query
    if (!isPostCall && data?.isMobile === true) {
      const url = new URL(`${this.endpoint}${path}`);
      url.searchParams.append('isMobile', 'true');
      path = `${url.pathname}${url.search}`;
      // remove /marina from the path
      if (path.includes('/marina')) {
        path = path.replace('/marina', '');
      }
    }
  
    const method = isPostCall ? 'POST' : 'GET';
  
    // Set headers for POST
    if (isPostCall) {
      headers.append('content-type', 'application/json');
    }
  
    const opts = Object.assign({}, this.requestOpts, {
      credentials: 'include',
      method,
      headers,
      body: isPostCall ? JSON.stringify(data) : undefined,
    }) as RequestInit;
  
    this.setParallelRequestsCount(this.parallelRequestsCount + 1);
  
    try {
      const response = await fetch(`${this.endpoint}${path}`, opts);
      const responseData = await response.json();
      if (response.ok) {
        return responseData as T;
      } else {
        const err = new ApiError(response, responseData);
        err.message = `Server response status ${response.status}`;
        if (err.response.status >= 500) {
          eventBus(Events.Bus.HTTP).emit(Events.Type.Error, err);
        }
        if (err.response.status == 401 && path.split('?')[0] != '/hello') {
          eventBus().emit(Events.Type.GoToWebsite, { url: window.location.href });
        }
        throw err;
      }
    }  finally {
      this.setParallelRequestsCount(this.parallelRequestsCount - 1);
    }
  }
  

  async uploadImageToOcr(path: string, formData: FormData) {
    const headers = new Headers();
  
    // Add extra headers except 'content-type' as the browser handles that for FormData
    this.requestOpts.extraHeaders.forEach(([h, c]) => headers.append(h, c));
  
    const method = 'POST';  // Always POST for file uploads
  
    const opts = Object.assign({}, this.requestOpts, {
      credentials: 'include',
      method,
      headers,
      body: formData,  // FormData is used as the body for the request
    }) as RequestInit;
  
    // Track parallel requests count
    this.setParallelRequestsCount(this.parallelRequestsCount + 1);
  
    try {
      // Make the fetch request to the given endpoint with the FormData
      const response = await fetch(`${this.endpoint}${path}`, opts);
      
      // Parse the JSON response
      const responseData = await response.json();
      
      // Handle response based on HTTP status
      if (response.ok) {
        return responseData;  // Return the response data if successful
      } else {
        // Handle errors
        const err = new ApiError(response, responseData);
        err.message = `Server response status ${response.status}`;
        
        if (err.response.status >= 500) {
          eventBus(Events.Bus.HTTP).emit(Events.Type.Error, err);
        }
  
        if (err.response.status === 401 && path.split('?')[0] !== '/hello') {
          eventBus().emit(Events.Type.GoToWebsite, { url: window.location.href });
        }
  
        throw err;
      }
    } finally {
      // Decrement the parallel request count
      this.setParallelRequestsCount(this.parallelRequestsCount - 1);
    }
  }
  
}

ApiService.define();
